// src/features/billing/BillingPoliciesTab.tsx
// Billing policies: default payment terms, tax rate, and invoice behavior for
// a scope entity (tenant, organization, department, ...). Tenant-wide admin
// config — billing_admin/billing_viewer on the tenant, not per-BillingAccount
// — so this lives in platform settings, not on any one billing account.
//
// Covers: ListBillingPolicies, CreateBillingPolicy, GetBillingPolicy (edit
// loads full record), GetBillingPolicyByEntity (lookup tool), UpdateBillingPolicy,
// DeleteBillingPolicy, ResolveBillingContext (preview).

import { useEffect, useState } from 'react';
import { observer } from '@react';
import { useBillingBillingPolicy, useI18n } from '@react/hooks';
import {
  Button,
  Input,
  Table,
  TableHeader,
  TableBody,
  TableRow,
  TableHead,
  TableCell,
  TableEmpty,
  ErrorAlert,
} from '@react/components';
import { ConfirmDialog } from '@/components/ConfirmDialog';
import { FormDialog } from '@/components/form';
import type { BillingPolicy } from '@core/types/billing';
import { errMessage } from './format';

interface PolicyForm {
  name: string;
  description: string;
  scope_entity_id: string;
  scope_entity_type: string;
  default_payment_terms_days: string;
  default_currency: string;
  default_tax_rate: string;
  tax_inclusive: boolean;
  auto_charge: boolean;
  auto_finalize: boolean;
  require_invoice_approval: boolean;
}

const EMPTY: PolicyForm = {
  name: '', description: '', scope_entity_id: '', scope_entity_type: 'tenant',
  default_payment_terms_days: '30', default_currency: 'USD', default_tax_rate: '',
  tax_inclusive: false, auto_charge: false, auto_finalize: false, require_invoice_approval: false,
};

export const BillingPoliciesTab = observer(() => {
  const store = useBillingBillingPolicy();
  const { t } = useI18n();
  const [editing, setEditing] = useState<BillingPolicy | null>(null);
  const [formOpen, setFormOpen] = useState(false);
  const [form, setForm] = useState<PolicyForm>(EMPTY);
  const [deleteTarget, setDeleteTarget] = useState<BillingPolicy | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [previewFor, setPreviewFor] = useState<string | null>(null);
  const [preview, setPreview] = useState<{ termsDays?: number; taxRate?: string; schemes: number } | null>(null);
  const [previewLoading, setPreviewLoading] = useState(false);

  // Lookup-by-entity tool
  const [lookupType, setLookupType] = useState('tenant');
  const [lookupId, setLookupId] = useState('');
  const [lookupResult, setLookupResult] = useState<BillingPolicy | null | undefined>(undefined);
  const [lookupLoading, setLookupLoading] = useState(false);

  useEffect(() => {
    store.ListBillingPolicies({ pagination: { cursor: '', limit: 50, direction: 'forward' }, sort: [], date_range: {} as any, filters: {} as any } as any);
  }, []);

  const openCreate = () => { setEditing(null); setForm(EMPTY); setFormOpen(true); };

  const openEdit = async (p: BillingPolicy) => {
    if (!p.entity?.id) return;
    try {
      const full = await store.GetBillingPolicy(p.entity.id);
      setEditing(full);
      setForm({
        name: full.name ?? '', description: full.description ?? '',
        scope_entity_id: full.scope_entity?.id ?? '', scope_entity_type: full.scope_entity_type ?? 'tenant',
        default_payment_terms_days: String(full.default_payment_terms_days ?? 30),
        default_currency: (full.default_currency as any)?.replace?.('CURRENCY_', '') || 'USD',
        default_tax_rate: full.default_tax_rate ?? '',
        tax_inclusive: !!full.tax_inclusive, auto_charge: !!full.auto_charge,
        auto_finalize: !!full.auto_finalize, require_invoice_approval: !!full.require_invoice_approval,
      });
      setFormOpen(true);
    } catch {
      // fall back to the row we already have
      setEditing(p);
      setForm({
        name: p.name ?? '', description: p.description ?? '',
        scope_entity_id: p.scope_entity?.id ?? '', scope_entity_type: p.scope_entity_type ?? 'tenant',
        default_payment_terms_days: String(p.default_payment_terms_days ?? 30),
        default_currency: (p.default_currency as any)?.replace?.('CURRENCY_', '') || 'USD',
        default_tax_rate: p.default_tax_rate ?? '',
        tax_inclusive: !!p.tax_inclusive, auto_charge: !!p.auto_charge,
        auto_finalize: !!p.auto_finalize, require_invoice_approval: !!p.require_invoice_approval,
      });
      setFormOpen(true);
    }
  };

  const submit = async () => {
    if (!form.name.trim() || (!editing && !form.scope_entity_id.trim())) return;
    setError(null);
    try {
      const termsDays = form.default_payment_terms_days ? Number(form.default_payment_terms_days) : undefined;
      const currency = form.default_currency ? (`CURRENCY_${form.default_currency.toUpperCase()}` as any) : undefined;
      if (editing?.entity?.id) {
        await store.UpdateBillingPolicy(editing.entity.id, {
          policy: {
            ...editing,
            name: form.name,
            description: form.description,
            default_payment_terms_days: termsDays,
            default_currency: currency,
            default_tax_rate: form.default_tax_rate || undefined,
            tax_inclusive: form.tax_inclusive,
            auto_charge: form.auto_charge,
            auto_finalize: form.auto_finalize,
            require_invoice_approval: form.require_invoice_approval,
          } as any,
          field_mask: [],
        } as any);
      } else {
        await store.CreateBillingPolicy({
          entity: {} as any,
          name: form.name,
          description: form.description,
          scope_entity: { id: form.scope_entity_id } as any,
          scope_entity_type: form.scope_entity_type,
          default_payment_terms_days: termsDays,
          default_currency: currency,
          default_tax_rate: form.default_tax_rate || undefined,
          tax_inclusive: form.tax_inclusive,
          auto_charge: form.auto_charge,
          auto_finalize: form.auto_finalize,
          require_invoice_approval: form.require_invoice_approval,
        } as any);
      }
      setFormOpen(false);
    } catch (err) {
      setError(errMessage(err, t('billing.policies.saveError')));
    }
  };

  const handleDelete = async () => {
    if (!deleteTarget?.entity?.id) return;
    await store.DeleteBillingPolicy(deleteTarget.entity.id);
    setDeleteTarget(null);
  };

  const handlePreview = async (p: BillingPolicy) => {
    if (!p.scope_entity) return;
    setPreviewFor(p.entity?.id ?? null);
    setPreviewLoading(true);
    setPreview(null);
    try {
      const res = await store.ResolveBillingContext({ scope_entity: p.scope_entity, scope_chain: [] } as any);
      setPreview({
        termsDays: res.effective_policy?.default_payment_terms_days,
        taxRate: res.effective_policy?.default_tax_rate,
        schemes: res.available_numbering_schemes?.length ?? 0,
      });
    } catch {
      setPreview(null);
    } finally {
      setPreviewLoading(false);
    }
  };

  const handleLookup = async () => {
    if (!lookupId.trim()) return;
    setLookupLoading(true);
    setLookupResult(undefined);
    try {
      const res = await store.GetBillingPolicyByEntity(lookupType.trim() || 'tenant', lookupId.trim());
      setLookupResult(res ?? null);
    } catch {
      setLookupResult(null);
    } finally {
      setLookupLoading(false);
    }
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <p className="text-sm text-muted-foreground">{t('billing.policies.subtitle')}</p>
        <Button size="sm" onClick={openCreate}>{t('billing.policies.create')}</Button>
      </div>

      {store.error ? <ErrorAlert error={store.error} dismissible onDismiss={() => store.clearError()} /> : null}

      <div className="rounded-lg border border-border p-4 space-y-2">
        <h4 className="text-sm font-medium text-foreground">{t('billing.policies.lookupTitle')}</h4>
        <div className="flex flex-wrap items-end gap-2">
          <Input label={t('billing.policies.scopeEntityType')} value={lookupType} onChange={(e) => setLookupType(e.target.value)} fullWidth={false} />
          <Input label={t('billing.policies.scopeEntityId')} value={lookupId} onChange={(e) => setLookupId(e.target.value)} fullWidth={false} />
          <Button size="sm" variant="secondary" onClick={handleLookup} isLoading={lookupLoading} disabled={!lookupId.trim()}>{t('billing.policies.lookup')}</Button>
        </div>
        {lookupResult !== undefined && (
          <p className="text-sm">
            {lookupResult ? (
              <span className="text-foreground">{lookupResult.name} — {t('billing.policies.scope')}: {lookupResult.scope_entity_type || '--'}</span>
            ) : (
              <span className="text-muted-foreground">{t('billing.policies.lookupEmpty')}</span>
            )}
          </p>
        )}
      </div>

      <Table striped hoverable>
        <TableHeader>
          <TableRow>
            <TableHead>{t('common.name')}</TableHead>
            <TableHead>{t('billing.policies.scope')}</TableHead>
            <TableHead>{t('billing.policies.paymentTerms')}</TableHead>
            <TableHead>{t('billing.policies.taxRate')}</TableHead>
            <TableHead className="text-right">{t('common.actions')}</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {store.isLoading ? (
            <TableRow><TableCell colSpan={5} className="text-center py-8">{t('common.loading')}</TableCell></TableRow>
          ) : store.items.length === 0 ? (
            <TableEmpty colSpan={5} message={t('billing.policies.empty')} />
          ) : (
            store.items.map((p) => (
              <TableRow key={p.entity?.id}>
                <TableCell>{p.name}</TableCell>
                <TableCell>{p.scope_entity_type || p.scope_entity?.type_name || '--'}</TableCell>
                <TableCell>{p.default_payment_terms_days != null ? t('billing.policies.netDays', { days: p.default_payment_terms_days }) : '--'}</TableCell>
                <TableCell>{p.default_tax_rate || '--'}{p.tax_inclusive ? ` (${t('billing.policies.taxInclusive')})` : ''}</TableCell>
                <TableCell className="text-right">
                  <div className="flex justify-end items-center gap-2">
                    {previewFor === p.entity?.id && preview && (
                      <span className="text-xs text-muted-foreground mr-1">
                        {t('billing.policies.previewResult', { days: preview.termsDays ?? '--', rate: preview.taxRate || '--', schemes: preview.schemes })}
                      </span>
                    )}
                    <Button variant="ghost" size="sm" isLoading={previewLoading && previewFor === p.entity?.id} onClick={() => handlePreview(p)}>{t('billing.policies.preview')}</Button>
                    <Button variant="ghost" size="sm" onClick={() => openEdit(p)}>{t('common.edit')}</Button>
                    <Button variant="ghost" size="sm" onClick={() => setDeleteTarget(p)}>{t('common.remove')}</Button>
                  </div>
                </TableCell>
              </TableRow>
            ))
          )}
        </TableBody>
      </Table>

      <FormDialog
        open={formOpen}
        onClose={() => setFormOpen(false)}
        onSubmit={submit}
        submitLabel={editing ? t('common.saveChanges') : t('common.create')}
        canSubmit={!!form.name.trim() && (!!editing || !!form.scope_entity_id.trim())}
        isSubmitting={store.isSaving}
        dirty={!!(form.name.trim() || form.description.trim())}
        aria-label={editing ? t('billing.policies.edit') : t('billing.policies.create')}
      >
        <div className="space-y-3 px-3.5 pb-4 pt-2">
          <h3 className="text-base font-semibold text-foreground">{editing ? t('billing.policies.edit') : t('billing.policies.create')}</h3>
          {error && <p className="text-sm text-destructive">{error}</p>}
          <Input label={t('common.name')} value={form.name} onChange={(e) => setForm((p) => ({ ...p, name: e.target.value }))} required />
          <Input label={t('common.description')} value={form.description} onChange={(e) => setForm((p) => ({ ...p, description: e.target.value }))} />
          {!editing && (
            <div className="grid grid-cols-2 gap-3">
              <Input label={t('billing.policies.scopeEntityId')} value={form.scope_entity_id} onChange={(e) => setForm((p) => ({ ...p, scope_entity_id: e.target.value }))} required />
              <Input label={t('billing.policies.scopeEntityType')} value={form.scope_entity_type} onChange={(e) => setForm((p) => ({ ...p, scope_entity_type: e.target.value }))} />
            </div>
          )}
          <div className="grid grid-cols-3 gap-3">
            <Input
              label={t('billing.policies.paymentTermsDays')}
              type="text"
              inputMode="numeric"
              value={form.default_payment_terms_days}
              onChange={(e) => setForm((p) => ({ ...p, default_payment_terms_days: e.target.value.replace(/[^0-9]/g, '') }))}
              placeholder="30"
            />
            <Input label={t('billing.policies.currency')} value={form.default_currency} onChange={(e) => setForm((p) => ({ ...p, default_currency: e.target.value.toUpperCase() }))} placeholder="USD" className="font-mono" />
            <Input label={t('billing.policies.taxRate')} value={form.default_tax_rate} onChange={(e) => setForm((p) => ({ ...p, default_tax_rate: e.target.value }))} placeholder="20.00" />
          </div>
          <div className="grid grid-cols-2 gap-2 pt-1">
            <label className="flex items-center gap-2 text-sm text-foreground">
              <input type="checkbox" checked={form.tax_inclusive} onChange={(e) => setForm((p) => ({ ...p, tax_inclusive: e.target.checked }))} className="h-4 w-4 rounded border-input text-primary focus:ring-ring" />
              {t('billing.policies.taxInclusive')}
            </label>
            <label className="flex items-center gap-2 text-sm text-foreground">
              <input type="checkbox" checked={form.auto_charge} onChange={(e) => setForm((p) => ({ ...p, auto_charge: e.target.checked }))} className="h-4 w-4 rounded border-input text-primary focus:ring-ring" />
              {t('billing.policies.autoCharge')}
            </label>
            <label className="flex items-center gap-2 text-sm text-foreground">
              <input type="checkbox" checked={form.auto_finalize} onChange={(e) => setForm((p) => ({ ...p, auto_finalize: e.target.checked }))} className="h-4 w-4 rounded border-input text-primary focus:ring-ring" />
              {t('billing.policies.autoFinalize')}
            </label>
            <label className="flex items-center gap-2 text-sm text-foreground">
              <input type="checkbox" checked={form.require_invoice_approval} onChange={(e) => setForm((p) => ({ ...p, require_invoice_approval: e.target.checked }))} className="h-4 w-4 rounded border-input text-primary focus:ring-ring" />
              {t('billing.policies.requireApproval')}
            </label>
          </div>
        </div>
      </FormDialog>

      <ConfirmDialog
        open={!!deleteTarget}
        title={t('billing.policies.deleteConfirmTitle')}
        destructive
        isConfirming={store.isDeleting}
        onConfirm={handleDelete}
        onCancel={() => setDeleteTarget(null)}
      />
    </div>
  );
});

export default BillingPoliciesTab;
